import { Transaction } from '../types/intellijetnet'

/**
 * Configuration for transaction consolidation
 */
export const CONSOLIDATION_CONFIG = {
  // Time window in days to consider transactions as "back-to-back"
  TIME_WINDOW_DAYS: 7,
  
  // Fields that should be merged with newest-wins precedence
  MERGEABLE_FIELDS: [
    'sold_price',
    'asking_price',
    'list_date',
    'purchase_date',
    'est_aftt',
    'est_cycles',
    'aputts_new',
    'base_iata',
    'base_icao',
    'base_name',
    'base_state',
    'base_country',
    'base_continent',
    'lifecycle',
    'for_sale',
    'market_status',
    'description'
  ] as const
}

/**
 * Consolidated transaction with metadata about source transactions
 */
export interface ConsolidatedTransaction extends Transaction {
  // Array of original API transaction IDs that were consolidated
  source_transaction_ids: number[]
  // Flag to indicate this is a consolidated transaction
  is_consolidated: boolean
  // Number of transactions that were consolidated
  consolidation_count: number
}

/**
 * Group transactions by aircraft ID and time window
 */
export function groupTransactionsByAircraftAndTime(
  transactions: Transaction[],
  timeWindowDays: number = CONSOLIDATION_CONFIG.TIME_WINDOW_DAYS
): Map<number, Transaction[][]> {
  const aircraftGroups = new Map<number, Transaction[][]>()
  
  // First, group by aircraft_id
  const byAircraft = new Map<number, Transaction[]>()
  
  transactions.forEach(transaction => {
    if (!byAircraft.has(transaction.aircraft_id)) {
      byAircraft.set(transaction.aircraft_id, [])
    }
    byAircraft.get(transaction.aircraft_id)!.push(transaction)
  })
  
  // Then, for each aircraft, group by time windows
  byAircraft.forEach((aircraftTransactions, aircraftId) => {
    // Sort by transaction date (newest first)
    aircraftTransactions.sort((a, b) => 
      new Date(b.trans_date).getTime() - new Date(a.trans_date).getTime()
    )
    
    const timeGroups: Transaction[][] = []
    
    aircraftTransactions.forEach(transaction => {
      const transDate = new Date(transaction.trans_date)
      
      // Find existing group within time window
      let foundGroup = false
      
      for (const group of timeGroups) {
        const groupDate = new Date(group[0].trans_date)
        const daysDiff = Math.abs(
          (transDate.getTime() - groupDate.getTime()) / (1000 * 60 * 60 * 24)
        )
        
        if (daysDiff <= timeWindowDays) {
          group.push(transaction)
          foundGroup = true
          break
        }
      }
      
      // If no group found, create new one
      if (!foundGroup) {
        timeGroups.push([transaction])
      }
    })
    
    aircraftGroups.set(aircraftId, timeGroups)
  })
  
  return aircraftGroups
}

/**
 * Merge multiple transactions into a single consolidated transaction
 * Newest transaction data takes precedence for all fields
 */
export function mergeTransactions(transactions: Transaction[]): ConsolidatedTransaction {
  if (transactions.length === 0) {
    throw new Error('Cannot merge empty transaction array')
  }
  
  if (transactions.length === 1) {
    return {
      ...transactions[0],
      source_transaction_ids: [transactions[0].trans_id],
      is_consolidated: false,
      consolidation_count: 1
    }
  }
  
  // Sort by transaction date (newest first)
  const sortedTransactions = [...transactions].sort((a, b) => 
    new Date(b.trans_date).getTime() - new Date(a.trans_date).getTime()
  )
  
  // Start with the newest transaction as base
  const baseTransaction = sortedTransactions[0]
  const consolidatedTransaction: ConsolidatedTransaction = {
    ...baseTransaction,
    source_transaction_ids: sortedTransactions.map(t => t.trans_id),
    is_consolidated: true,
    consolidation_count: transactions.length
  }

  // For each field, use the newest non-null value across all transactions
  CONSOLIDATION_CONFIG.MERGEABLE_FIELDS.forEach(field => {
    // Find the newest transaction that has a non-null value for this field
    for (const transaction of sortedTransactions) {
      if (transaction[field] !== null && transaction[field] !== undefined) {
        ;(consolidatedTransaction as any)[field] = transaction[field]
        break // Use the first (newest) non-null value found
      }
    }
  })
  
  return consolidatedTransaction
}

/**
 * Consolidate all transactions, returning an array of consolidated transactions
 */
export function consolidateTransactions(
  transactions: Transaction[],
  timeWindowDays: number = CONSOLIDATION_CONFIG.TIME_WINDOW_DAYS
): ConsolidatedTransaction[] {
  const groupedTransactions = groupTransactionsByAircraftAndTime(transactions, timeWindowDays)
  const consolidatedTransactions: ConsolidatedTransaction[] = []
  
  groupedTransactions.forEach((timeGroups, _aircraftId) => {
    timeGroups.forEach(group => {
      const consolidated = mergeTransactions(group)
      consolidatedTransactions.push(consolidated)
    })
  })
  
  // Sort final result by transaction date (newest first)
  consolidatedTransactions.sort((a, b) => 
    new Date(b.trans_date).getTime() - new Date(a.trans_date).getTime()
  )
  
  return consolidatedTransactions
}

/**
 * Get consolidation statistics for debugging/logging
 */
export function getConsolidationStats(
  originalTransactions: Transaction[],
  consolidatedTransactions: ConsolidatedTransaction[]
): {
  originalCount: number
  consolidatedCount: number
  reductionCount: number
  reductionPercentage: number
  consolidatedGroups: number
  averageGroupSize: number
} {
  const consolidatedGroups = consolidatedTransactions.filter(t => t.is_consolidated).length
  const totalConsolidatedTransactions = consolidatedTransactions.reduce(
    (sum, t) => sum + t.consolidation_count, 0
  )
  
  return {
    originalCount: originalTransactions.length,
    consolidatedCount: consolidatedTransactions.length,
    reductionCount: originalTransactions.length - consolidatedTransactions.length,
    reductionPercentage: originalTransactions.length > 0 
      ? ((originalTransactions.length - consolidatedTransactions.length) / originalTransactions.length) * 100 
      : 0,
    consolidatedGroups,
    averageGroupSize: consolidatedGroups > 0 
      ? totalConsolidatedTransactions / consolidatedGroups 
      : 1
  }
}
